import CategoryBarChartCard from "../../../components/analytics/CategoryBarChartCard";
import { ChartSkeleton } from "../../../components/analytics/ChartSkeleton";
import SectionAnalytics from "../../../components/analytics/SectionAnalytics";
import { trpc } from "../../../trpc";
import { transformDataForRecharts } from "../../../utils";

const PRICE_BUCKET_ORDER = ["<1M", "1-2M", "2-3M", "3-4M", "4-5M", ">5M"] as const;

const PROPERTY_TYPE_DISPLAY: Record<string, string> = {
  apartment: "Apartamento",
  studio: "Estudio",
  house: "Casa",
  room: "Habitación",
  office: "Oficina",
  commercial_space: "Local",
  warehouse: "Bodega",
  medical_office: "Consultorio",
  building: "Edificio",
  farm: "Finca",
  hotel: "Hotel",
  lot: "Lote",
  parking: "Parqueadero",
};

const capitalize = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const toTopCategoryData = (map: Record<string, number> | undefined, limit = 10) => {
  if (!map) return [];
  return transformDataForRecharts(map)
    .map((entry) => ({ name: capitalize(entry.name), value: entry.value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

const toOrderedPriceData = (map: Record<string, number> | undefined) => {
  if (!map) return [];
  return PRICE_BUCKET_ORDER.map((bucket) => ({
    name: bucket,
    value: map[bucket] ?? 0,
  }));
};

const toOrderedBedroomsData = (map: Record<string, number> | undefined) => {
  if (!map) return [];
  return transformDataForRecharts(map)
    .map((entry) => ({ name: entry.name, value: entry.value, sortKey: Number(entry.name) }))
    .filter((entry) => !Number.isNaN(entry.sortKey))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ name, value }) => ({ name, value }));
};

const toPropertyTypeData = (map: Record<string, number> | undefined) => {
  if (!map) return [];
  return transformDataForRecharts(map)
    .map((entry) => ({
      name: PROPERTY_TYPE_DISPLAY[entry.name.toLowerCase()] ?? capitalize(entry.name),
      value: entry.value,
    }))
    .sort((a, b) => b.value - a.value);
};

type Props = {
  startDate: string;
  endDate: string;
  merchantId: string;
};

const AnalyticsDemandContainer = ({ startDate, endDate, merchantId }: Props) => {
  const { data, isLoading } = trpc.analytics.searchQueries.useQuery(
    { merchantId, startDate, endDate },
    { enabled: !!merchantId }
  );

  if (isLoading) {
    return (
      <div className="flex grow h-full flex-col gap-6">
        <SectionAnalytics title="Ubicación" gridClassName="grid-cols-1 md:grid-cols-2">
          <ChartSkeleton height="h-[260px]" />
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
        <SectionAnalytics
          title="Características"
          gridClassName="grid-cols-1 md:grid-cols-3"
        >
          <ChartSkeleton height="h-[220px]" />
          <ChartSkeleton height="h-[220px]" />
          <ChartSkeleton height="h-[220px]" />
        </SectionAnalytics>
      </div>
    );
  }

  const cityData = toTopCategoryData(data?.searchQueriesByCity);
  const neighborhoodData = toTopCategoryData(data?.searchQueriesByNeighborhood);
  const priceData = toOrderedPriceData(data?.searchQueriesByPrice);
  const bedroomsData = toOrderedBedroomsData(data?.searchQueriesByBedrooms);
  const propertyTypeData = toPropertyTypeData(data?.searchQueriesByPropertyType);

  return (
    <div className="flex grow h-full flex-col gap-6">
      <SectionAnalytics title="Ubicación" gridClassName="grid-cols-1 md:grid-cols-2">
        <CategoryBarChartCard
          title="Búsquedas por ciudad"
          subtitle="Ciudades mencionadas con más frecuencia"
          data={cityData}
          orientation="horizontal"
          height={260}
        />
        <CategoryBarChartCard
          title="Búsquedas por barrio"
          subtitle="Top 10 de barrios más consultados"
          data={neighborhoodData}
          orientation="horizontal"
          height={260}
        />
      </SectionAnalytics>
      <SectionAnalytics title="Características" gridClassName="grid-cols-1 md:grid-cols-3">
        <CategoryBarChartCard
          title="Búsquedas por precio"
          subtitle="Rango de precio solicitado"
          data={priceData}
          height={220}
        />
        <CategoryBarChartCard
          title="Búsquedas por habitaciones"
          subtitle="Número de habitaciones preferido"
          data={bedroomsData}
          height={220}
        />
        <CategoryBarChartCard
          title="Búsquedas por tipo de propiedad"
          subtitle="Tipo de inmueble solicitado"
          data={propertyTypeData}
          height={220}
        />
      </SectionAnalytics>
    </div>
  );
};

export default AnalyticsDemandContainer;
